import React, {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Flex, Text, Button } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { DATA_VAULT_CONTEXT } from "../constants";
import CreateCustomFieldModal from "./components/CreateCustomFieldModal";
import CreateSectionModal from "./components/CreateSectionModal";
import DeleteCustomFieldModal from "./components/DeleteCustomFieldModal";
import EditCustomFieldModal from "./components/EditCustomFieldModal";
import { SectionsView } from "./components/SectionsView";
import { TabsView } from "./components/TabsView";
import { useSections } from "./hooks/useSections";
import { CustomField } from "./types";

interface Props {
  isActive?: boolean;
}

export interface CustomFieldsRef {
  openCreateModal: () => void;
}

const CustomFields = forwardRef<CustomFieldsRef, Props>(({ isActive = true }: Props, ref) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateSectionModal, setShowCreateSectionModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [deletingField, setDeletingField] = useState<CustomField | null>(null);
  const [activeTab, setActiveTab] = useState<string>("");

  // Fetch custom fields and sections
  const { sections, loading, error, sectionsWithFields, tabsWithSections } =
    useSections(DATA_VAULT_CONTEXT);

  const handleCreateField = useCallback(() => {
    setShowCreateModal(true);
  }, []);

  const handleCreateSection = useCallback(() => {
    setShowCreateSectionModal(true);
  }, []);

  const handleEditField = useCallback((field: CustomField) => {
    setEditingField(field);
  }, []);

  const handleDeleteField = useCallback((field: CustomField) => {
    setDeletingField(field);
  }, []);

  const handleCloseModals = useCallback(() => {
    setShowCreateModal(false);
    setShowCreateSectionModal(false);
    setEditingField(null);
    setDeletingField(null);
  }, []);

  // Expose methods to parent component via ref
  useImperativeHandle(
    ref,
    () => ({
      openCreateModal: handleCreateField,
    }),
    [handleCreateField],
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + N to create new field
      if ((event.ctrlKey || event.metaKey) && event.key === "n") {
        event.preventDefault();
        handleCreateField();
      }
      // Ctrl/Cmd + Shift + N to create new section
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "N") {
        event.preventDefault();
        handleCreateSection();
      }
      // Escape to close modals
      if (event.key === "Escape") {
        handleCloseModals();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleCreateField, handleCreateSection, handleCloseModals]);

  // Close modals when tab becomes inactive
  useEffect(() => {
    if (!isActive) {
      handleCloseModals();
    }
  }, [isActive, handleCloseModals]);

  // Cleanup: Close all modals when component unmounts (e.g., when switching tabs)
  useEffect(() => {
    return () => {
      // Close all modals when component unmounts
      handleCloseModals();
    };
  }, [handleCloseModals]);

  useEffect(() => {
    if (
      (tabsWithSections || []).length > 0 &&
      (!activeTab || !tabsWithSections.find((tab) => tab.tabName === activeTab))
    ) {
      setActiveTab(tabsWithSections[0].tabName);
    }
  }, [tabsWithSections, activeTab]);

  const getTabsForSelection = () => {
    return tabsWithSections
      .map(({ tabName }) => ({ name: tabName }))
      .filter((tab) => tab.name !== "Other");
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Flex justifyContent="center" alignItems="center" style={{ padding: SPACING.LARGE }}>
          <Text variant="regular-md">Loading sections and fields...</Text>
        </Flex>
      );
    }

    if (error) {
      return (
        <Flex flexDirection="column" alignItems="center" style={{ padding: SPACING.LARGE }}>
          <Text variant="regular-md" color="error-500">
            Error loading data: {error.message}
          </Text>
        </Flex>
      );
    }

    if (tabsWithSections.length > 0) {
      return (
        <TabsView
          tabsWithSections={tabsWithSections}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onEditField={handleEditField}
          onDeleteField={handleDeleteField}
        />
      );
    }

    return (
      <SectionsView
        sectionsWithFields={sectionsWithFields}
        onEditField={handleEditField}
        onDeleteField={handleDeleteField}
      />
    );
  };

  return (
    <div
      role="main"
      aria-label="Custom Fields content"
      style={{
        padding: SPACING.LARGE,
        height: "100%",
        backgroundColor: "#f8f9fa",
      }}
    >
      {/* Page Header */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: SPACING.LARGE }}
      >
        <Text variant="regular-xl" style={{ fontWeight: "600" }}>
          Custom fields
        </Text>
        <Flex gap={SPACING.SMALL}>
          <Button
            variant="neutral"
            onClick={handleCreateSection}
            data-testid="create-section-button"
          >
            Create section
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateField}
            data-testid="create-custom-field-button"
          >
            Create custom field
          </Button>
        </Flex>
      </Flex>

      {/* Content Area */}
      <Flex aria-label="Custom fields sections" flexDirection="column">
        {renderContent()}
      </Flex>

      {/* Modals */}
      <CreateCustomFieldModal
        show={showCreateModal}
        onHide={handleCloseModals}
        sections={sections}
      />

      <EditCustomFieldModal
        sections={sections}
        show={!!editingField}
        field={editingField}
        onHide={handleCloseModals}
      />

      <CreateSectionModal
        show={showCreateSectionModal}
        onHide={handleCloseModals}
        tabs={getTabsForSelection()}
      />

      <DeleteCustomFieldModal
        show={!!deletingField}
        field={deletingField}
        onHide={handleCloseModals}
      />
    </div>
  );
});

CustomFields.displayName = "CustomFields";

export default CustomFields;
